import { LubooDialog } from './luboo-dialog.js';
import { createInviteIdentityField } from './invite-identity-field.js';
import { setupChannel, destroyChannel, lubooRequest } from '../mixins/data-channel.mixin.js';

/**
 * <account-team-list> — Account Team management UI.
 *
 * Lists current account members + pending invitations. Owner / admins can
 * invite new members, change roles, remove members, and cancel pending
 * invitations.
 *
 * Live updates use surgical row insert/patch/remove via BroadcastChannel
 * messages routed by the data-channel mixin. The full-table innerHTML
 * rebuild from refresh() is reserved for first load and error retries —
 * peer-tab mutations no longer flash the whole UI.
 *
 * @attr {string} account-id - Account UUID (required)
 * @attr {boolean} can-manage - Whether viewer has MANAGE_TEAM permission
 */

const ACCOUNT_ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

const INVITABLE_ROLES = ['admin', 'member', 'viewer']; // can't invite a new Owner

class AccountTeamList extends HTMLElement {
  static get observedAttributes() {
    return ['account-id', 'can-manage'];
  }

  // Mixin methods attached as instance fields (esbuild ordering safe).
  setupChannel = setupChannel;
  destroyChannel = destroyChannel;

  _messageMap = {
    member_invited: this._onMemberInvited,
    member_updated: this._onMemberUpdated,
    member_removed: this._onMemberRemoved,
    invitation_cancelled: this._onInvitationCancelled,
  };

  constructor() {
    super();
    this._initialized = false;
    this._members = [];
    this._invitations = [];
    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this._scopedClick = (e) => {
      if (this.contains(e.target)) this._onClick(e);
    });
    document.addEventListener('change', this._scopedChange = (e) => {
      if (this.contains(e.target)) this._onChange(e);
    });
    if (!this._initialized) {
      this._initialized = true;
      this._renderLoading();
    }
    if (this.accountId) {
      this.setupChannel(`luboo:account:${this.accountId}`);
      this.refresh();
    }
  }

  disconnectedCallback() {
    if (this._scopedClick) document.removeEventListener('click', this._scopedClick);
    if (this._scopedChange) document.removeEventListener('change', this._scopedChange);
    this._scopedClick = null;
    this._scopedChange = null;
    this.destroyChannel();
  }

  get accountId() {
    return this.getAttribute('account-id');
  }

  get canManage() {
    return this.getAttribute('can-manage') === 'true';
  }

  async refresh() {
    try {
      const channel = `luboo:account:${this.accountId}`;
      const [members, invitations] = await Promise.all([
        lubooRequest(
          channel,
          'list',
          { accountId: this.accountId },
          { group: 'member' }
        ),
        this.canManage
          ? lubooRequest(
              channel,
              'list',
              { accountId: this.accountId, status: 'pending' },
              { group: 'account_invitation' }
            ).catch(() => [])
          : Promise.resolve([]),
      ]);
      this._members = members || [];
      this._invitations = invitations || [];
      this._render();
    } catch (err) {
      this._renderError(String(err?.message || err));
    }
  }

  _onClick(e) {
    if (e.target.closest('[data-account-team-action="invite"]')) {
      e.preventDefault();
      this._openInviteDialog();
      return;
    }
    const removeBtn = e.target.closest(
      '[data-account-team-action="remove-member"]'
    );
    if (removeBtn) {
      e.preventDefault();
      this._handleRemoveMember(removeBtn.getAttribute('data-user-id'));
      return;
    }
    const cancelBtn = e.target.closest(
      '[data-account-team-action="cancel-invitation"]'
    );
    if (cancelBtn) {
      e.preventDefault();
      this._handleCancelInvitation(
        cancelBtn.getAttribute('data-invitation-id')
      );
      return;
    }
    const copyBtn = e.target.closest(
      '[data-account-team-action="copy-invite-link"]'
    );
    if (copyBtn) {
      e.preventDefault();
      this._handleCopyInviteLink(copyBtn.getAttribute('data-token'));
    }
  }

  _onChange(e) {
    if (
      e.target.matches('[data-account-team-action="change-role"]')
    ) {
      const userId = e.target.closest('[data-account-team-member]')
        .getAttribute('data-user-id');
      this._handleChangeRole(userId, e.target.value);
    }
  }

  async _openInviteDialog() {
    // Fetch the live role list so custom roles show up too
    let roles = [];
    try {
      roles = await lubooRequest(
        `luboo:account:${this.accountId}`,
        'list',
        { accountId: this.accountId },
        { group: 'role' }
      );
    } catch {}
    if (!Array.isArray(roles)) roles = [];
    // Filter: never let someone invite a new Owner via this flow
    roles = roles.filter(
      (r) => !(r.is_system && r.system_kind === 'owner')
    );

    const dlg = document.createElement('luboo-dialog');
    dlg.setAttribute('heading', 'Invite team member');

    const intro = document.createElement('p');
    intro.textContent =
      "Search by username to invite a registered user, or switch to email to invite someone new. An invitation email will be sent automatically.";
    dlg.appendChild(intro);

    const identityField = createInviteIdentityField({
      parent: dlg,
      existingMemberIds: this._members.map((m) => m.user_id),
    });

    const roleLabel = document.createElement('label');
    roleLabel.style.display = 'block';
    roleLabel.style.marginTop = '0.5rem';
    const roleSpan = document.createElement('span');
    roleSpan.textContent = 'Role';
    roleLabel.appendChild(roleSpan);
    const roleSelect = document.createElement('select');
    roleSelect.className = 'dialog-input';
    // Default selection: Member if it exists, else first
    let defaultRoleId = roles.find(
      (r) => r.is_system && r.system_kind === 'member'
    )?.id;
    if (!defaultRoleId && roles.length) defaultRoleId = roles[0].id;
    for (const r of roles) {
      const opt = document.createElement('option');
      opt.value = r.id;
      const tag = r.is_system ? '' : ' (custom)';
      opt.textContent = `${r.name}${tag}`;
      opt.dataset.systemKind = r.system_kind || '';
      opt.dataset.isSystem = String(!!r.is_system);
      if (r.id === defaultRoleId) opt.selected = true;
      roleSelect.appendChild(opt);
    }
    roleLabel.appendChild(roleSelect);
    dlg.appendChild(roleLabel);

    const cancelBtn = document.createElement('button');
    cancelBtn.setAttribute('slot', 'actions');
    cancelBtn.setAttribute('value', 'cancel');
    cancelBtn.textContent = 'Cancel';
    dlg.appendChild(cancelBtn);

    const sendBtn = document.createElement('button');
    sendBtn.setAttribute('slot', 'actions');
    sendBtn.setAttribute('value', 'confirm');
    sendBtn.setAttribute('type', 'submit');
    sendBtn.textContent = 'Send invite';
    dlg.appendChild(sendBtn);

    document.body.appendChild(dlg);

    let identity = null;
    let selectedOpt = null;
    try {
      const result = await dlg.open();
      if (result !== 'confirm') return;
      const err = identityField.validate();
      if (err) {
        await LubooDialog.alert(err, { heading: 'Invite failed' });
        return;
      }
      identity = identityField.getIdentity();
      selectedOpt = roleSelect.options[roleSelect.selectedIndex];
    } finally {
      if (typeof dlg._destroy === 'function') dlg._destroy();
    }
    if (!identity || !selectedOpt) return;

    const isSystem = selectedOpt.dataset.isSystem === 'true';
    const systemKind = selectedOpt.dataset.systemKind;
    const payload = { ...identity };
    if (isSystem && systemKind) {
      payload.role = systemKind; // legacy enum, system kind matches
    } else {
      payload.role = 'viewer'; // fallback enum (lowest); role_id is authoritative
      payload.role_id = selectedOpt.value;
    }

    try {
      await lubooRequest(
        `luboo:account:${this.accountId}`,
        'invite',
        { accountId: this.accountId, body: {
          ...payload,
          invite_url_template: `${window.location.origin}/invite/{token}`,
        } },
        { group: 'member' }
      );
    } catch (error) {
      await LubooDialog.alert(error?.message || 'Could not create invitation.', {
        heading: 'Invite failed',
      });
      return;
    }
    await this.refresh();
  }

  async _handleChangeRole(userId, newRole) {
    try {
      await lubooRequest(
        `luboo:account:${this.accountId}`,
        'update',
        {
          accountId: this.accountId,
          userId,
          body: { role: newRole },
        },
        { group: 'member' }
      );
    } catch (error) {
      await LubooDialog.alert(error?.message || 'Could not change role.', {
        heading: 'Update failed',
      });
      await this.refresh();
      return;
    }
    await this.refresh();
  }

  async _handleRemoveMember(userId) {
    const member = this._members.find((m) => m.user_id === userId);
    const displayName =
      member && (member.first_name || member.username || member.email);
    const confirmed = await LubooDialog.confirm(
      `Remove ${displayName || 'this member'} from the account? They will lose access immediately.`,
      {
        heading: 'Remove member?',
        okLabel: 'Remove',
        cancelLabel: 'Cancel',
        danger: true,
      }
    );
    if (!confirmed) return;
    try {
      await lubooRequest(
        `luboo:account:${this.accountId}`,
        'remove',
        { accountId: this.accountId, userId },
        { group: 'member' }
      );
    } catch (error) {
      await LubooDialog.alert(error?.message || 'Could not remove member.', {
        heading: 'Remove failed',
      });
      return;
    }
    await this.refresh();
  }

  async _handleCancelInvitation(invitationId) {
    const confirmed = await LubooDialog.confirm(
      'Cancel this pending invitation? The invite link will stop working.',
      {
        heading: 'Cancel invitation?',
        okLabel: 'Cancel invite',
        cancelLabel: 'Keep',
        danger: true,
      }
    );
    if (!confirmed) return;
    try {
      await lubooRequest(
        `luboo:account:${this.accountId}`,
        'cancel',
        { accountId: this.accountId, invitationId },
        { group: 'account_invitation' }
      );
    } catch (error) {
      await LubooDialog.alert(error?.message || 'Could not cancel invitation.', {
        heading: 'Cancel failed',
      });
      return;
    }
    await this.refresh();
  }

  async _handleCopyInviteLink(token) {
    const url = `${window.location.origin}/invite/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      await LubooDialog.alert(
        `Copied: ${url}\n\nShare this link with the invitee. They'll be asked to sign in or sign up, then accept the invitation.`,
        { heading: 'Invite link copied' }
      );
    } catch {
      await LubooDialog.alert(url, { heading: 'Invite link' });
    }
  }

  _escape(s) {
    return String(s || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  _displayName(m) {
    if (m.first_name && m.last_name) return `${m.first_name} ${m.last_name}`;
    if (m.first_name) return m.first_name;
    return m.username || m.email || m.user_id;
  }

  _renderLoading() {
    this.innerHTML = `
      <div class="account-team-loading luboo-empty-state">
        <p>Loading team…</p>
      </div>
    `;
  }

  _renderError(msg) {
    this.innerHTML = `
      <div class="account-team-error luboo-empty-state">
        <p>Couldn't load team: ${this._escape(msg)}</p>
      </div>
    `;
  }

  /**
   * Full render. Builds the shell + initial row sets once. Subsequent
   * live updates patch member / invitation rows in place via
   * _patchMemberRow / _insertMemberRow / _removeMemberRow (and the
   * invite variants) so peer-tab mutations don't flash the whole list.
   *
   * refresh() still calls _render() so a manual reload (or first mount
   * after a permission attribute flip) rebuilds cleanly.
   */
  _render() {
    const invitations = this._invitations.filter(
      (i) => i.status === 'pending'
    );
    const inviteBtn = this.canManage
      ? `<button class="btn btn-primary"
                data-account-team-action="invite">
          <svg-icon icon="luboo:user-plus"></svg-icon> Invite member
        </button>`
      : '';
    // Always emit both sections (members + pending invites) so live
    // event handlers can target them by class without re-rendering the
    // shell when a section transitions from empty to populated.
    this.innerHTML = `
      <div class="account-team">
        <div class="account-team-toolbar">${inviteBtn}</div>
        <div class="account-team-section">
          <h4>Members</h4>
          <ul class="account-team-members"></ul>
        </div>
        <div class="account-team-section account-team-invitations-section"
             ${invitations.length ? '' : 'hidden'}>
          <h4>Pending invitations</h4>
          <ul class="account-team-invitations"></ul>
        </div>
      </div>
    `;
    const membersUl = this.querySelector('.account-team-members');
    if (membersUl) {
      for (const m of this._members) {
        membersUl.insertAdjacentHTML('beforeend', this._memberRowHtml(m));
      }
    }
    const invitesUl = this.querySelector('.account-team-invitations');
    if (invitesUl) {
      for (const i of invitations) {
        invitesUl.insertAdjacentHTML('beforeend', this._inviteRowHtml(i));
      }
    }
  }

  // ── Real-time event handlers (BCM-routed) ────────────────────────────

  /**
   * A new pending invitation landed for this account. Surgically append
   * the row to the invitations list without touching the members list.
   */
  _onMemberInvited(invite) {
    if (!invite?.id) return;
    if (invite.status && invite.status !== 'pending') return;
    if (this._invitations.some((i) => i.id === invite.id)) return;
    this._invitations.push(invite);
    this._appendInviteRow(invite);
  }

  /**
   * An existing member's row needs a fresh role / details. Patch it in
   * place; insert if it's a brand-new member (e.g. invite-accepted on
   * another tab).
   */
  _onMemberUpdated(member) {
    if (!member?.user_id) return;
    const index = this._members.findIndex((m) => m.user_id === member.user_id);
    if (index === -1) {
      this._members.push(member);
      this._appendMemberRow(member);
      return;
    }
    this._members[index] = member;
    this._patchMemberRow(member);
  }

  /**
   * A member was removed. Payload is just {id} from the server.
   */
  _onMemberRemoved(payload) {
    const userId = payload?.id;
    if (!userId) return;
    const index = this._members.findIndex((m) => m.user_id === userId);
    if (index === -1) return;
    this._members.splice(index, 1);
    this._removeMemberRow(userId);
  }

  /**
   * A pending invitation was cancelled. Payload is {id}.
   */
  _onInvitationCancelled(payload) {
    const invitationId = payload?.id;
    if (!invitationId) return;
    const index = this._invitations.findIndex((i) => i.id === invitationId);
    if (index === -1) return;
    this._invitations.splice(index, 1);
    this._removeInviteRow(invitationId);
  }

  // ── Surgical DOM helpers ─────────────────────────────────────────────

  _patchMemberRow(member) {
    const row = this.querySelector(
      `.account-team-member-row[data-user-id="${CSS.escape(member.user_id)}"]`
    );
    if (!row) return;
    const fresh = document.createElement('template');
    fresh.innerHTML = this._memberRowHtml(member).trim();
    const next = fresh.content.firstElementChild;
    if (next) row.replaceWith(next);
  }

  _appendMemberRow(member) {
    const ul = this.querySelector('.account-team-members');
    if (!ul) {
      // Shell isn't mounted yet — fall back to full render.
      this._render();
      return;
    }
    ul.insertAdjacentHTML('beforeend', this._memberRowHtml(member));
  }

  _removeMemberRow(userId) {
    const row = this.querySelector(
      `.account-team-member-row[data-user-id="${CSS.escape(userId)}"]`
    );
    if (row) row.remove();
  }

  _appendInviteRow(invite) {
    const ul = this.querySelector('.account-team-invitations');
    const section = this.querySelector('.account-team-invitations-section');
    if (!ul || !section) {
      this._render();
      return;
    }
    section.removeAttribute('hidden');
    ul.insertAdjacentHTML('beforeend', this._inviteRowHtml(invite));
  }

  _removeInviteRow(invitationId) {
    const row = this.querySelector(
      `.account-team-invite-row[data-invitation-id="${CSS.escape(invitationId)}"]`
    );
    if (row) row.remove();
    const ul = this.querySelector('.account-team-invitations');
    const section = this.querySelector('.account-team-invitations-section');
    if (section && ul && ul.children.length === 0) {
      section.setAttribute('hidden', '');
    }
  }

  _memberRowHtml(m) {
    const role = m.role || 'member';
    const name = this._displayName(m);
    const subtitle = m.email && name !== m.email ? m.email : '';
    const canEdit = this.canManage && role !== 'owner';
    const roleSelectOptions = ['owner', 'admin', 'member', 'viewer']
      .map(
        (v) =>
          `<option value="${v}" ${v === role ? 'selected' : ''}>${ACCOUNT_ROLE_LABELS[v]}</option>`
      )
      .join('');
    return `
      <li class="account-team-member-row"
          data-account-team-member
          data-user-id="${this._escape(m.user_id)}">
        <div class="account-team-member-avatar">
          <topographic-identicon-or-image
            username="${this._escape(m.username || m.email || m.user_id)}"
            ${m.avatar_url ? `url="${this._escape(m.avatar_url)}"` : ''}
            num-peaks="4" num-contours="12"
            peak-height-max="0.9" peak-spread="23"
            peak-spacing="1" stroke-width="0.9"
            show-peaks="false"
            bg-color="var(--panel-color)"
            line-color="var(--text-color)"
            accent-color="var(--highlight-color)">
          </topographic-identicon-or-image>
        </div>
        <div class="account-team-member-info">
          <div class="account-team-member-name">${this._escape(name)}</div>
          ${subtitle ? `<div class="account-team-member-email">${this._escape(subtitle)}</div>` : ''}
        </div>
        <div class="account-team-member-role">
          ${
            canEdit
              ? `<select class="account-team-role-select"
                       data-account-team-action="change-role"
                       aria-label="Change role for ${this._escape(name)}">${roleSelectOptions}</select>`
              : `<span class="role-badge role-badge-system">${ACCOUNT_ROLE_LABELS[role] || role}</span>`
          }
        </div>
        <div class="account-team-member-actions">
          ${
            canEdit
              ? `<button class="btn btn-sm btn-secondary"
                       data-account-team-action="remove-member"
                       data-user-id="${this._escape(m.user_id)}"
                       aria-label="Remove ${this._escape(name)}">
                  <svg-icon icon="luboo:trash"></svg-icon>
                </button>`
              : ''
          }
        </div>
      </li>
    `;
  }

  _inviteRowHtml(i) {
    return `
      <li class="account-team-invite-row"
          data-invitation-id="${this._escape(i.id)}">
        <div class="account-team-invite-email">${this._escape(i.email)}</div>
        <span class="role-badge role-badge-custom">${ACCOUNT_ROLE_LABELS[i.role] || i.role}</span>
        <span class="account-team-invite-meta">Invited ${this._formatDate(i.created_at)}</span>
        <div class="account-team-invite-actions">
          <button class="btn btn-sm btn-secondary"
                  data-account-team-action="copy-invite-link"
                  data-token="${this._escape(i.token)}">
            Copy link
          </button>
          <button class="btn btn-sm btn-secondary"
                  data-account-team-action="cancel-invitation"
                  data-invitation-id="${this._escape(i.id)}"
                  aria-label="Cancel invitation for ${this._escape(i.email)}">
            <svg-icon icon="luboo:x"></svg-icon>
          </button>
        </div>
      </li>
    `;
  }

  _formatDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return iso;
    }
  }
}

export { AccountTeamList };
